import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { AGGClient } from "src/clients";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import { Button } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { experiments, ExperimentView } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { getIsSMBSalesLead } from "./contact-sales-cta-helpers";

const fullWidthStyles = css({
    maxWidth: "none",
    width: "100%"
});

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const { accountId } = useLoggedInUser();
    // feature gate has configuration to only be enabled on specified page paths
    // https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/wac_sales_inbound_leads
    const isFeatureGateEnabled = checkFeatureFlagGate(
        featureFlags.wacSalesInboundLeads
    );

    const [isSalesLead, setIsSalesLead] = useState<boolean>(false);

    useEffect(() => {
        if (!isFeatureGateEnabled || !accountId) return;
        const aggClient = new AGGClient({
            gatewayUrl:
                process.env["NODE_ENV"] === "production"
                    ? "https://www.atlassian.com/gateway/api/graphql"
                    : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
        });

        const isSMBSalesLead = async () => {
            getIsSMBSalesLead({
                accountId,
                aggClient
            })
                .then((response) => {
                    setIsSalesLead(!!response);
                })
                .catch(() => {
                    console.error(
                        "Error fetching GUPS user profile for SMB Sales lead"
                    );
                });
        };
        isSMBSalesLead();
    }, [accountId, isFeatureGateEnabled]);

    if (!accountId || !isFeatureGateEnabled) {
        return;
    }

    return isSalesLead && <CtaButtonRenderer isFullWidth={isFullWidth} />;
};

const CtaButtonRenderer = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const localizedHref = useLocalizedHref(
        "/enterprise/contact?formType=product-features"
    );

    useEffect(() => {
        if (analyticsClient) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "button",
                actionSubjectId: "contactSalesCta",
                source: "globalNav",
                attributes: {
                    isMobile: !!isFullWidth
                }
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent, isFullWidth]);

    return (
        <ExperimentView
            experiment={experiments.gwp8111SalesInboundLeadsCta}
            render={(cohort) => {
                if (cohort === "test1") {
                    return (
                        <CtaButton
                            isFullWidth={isFullWidth}
                            localizedHref={localizedHref}
                            label="Contact sales"
                            buttonVariant="primary_alt"
                        />
                    );
                } else if (cohort === "test2") {
                    return (
                        <CtaButton
                            isFullWidth={isFullWidth}
                            localizedHref={localizedHref}
                            label="Contact us"
                            buttonVariant="primary_alt"
                        />
                    );
                } else {
                    return (
                        <CtaButton
                            isFullWidth={isFullWidth}
                            localizedHref={localizedHref}
                            label="Questions?"
                            buttonVariant="secondary"
                        />
                    );
                }
            }}
        />
    );
};

const CtaButton = ({
    isFullWidth,
    localizedHref,
    label,
    buttonVariant
}: {
    isFullWidth?: boolean;
    localizedHref: string;
    label: string;
    buttonVariant: "secondary" | "primary_alt";
}) => (
    <Button
        variant={buttonVariant}
        css={isFullWidth && fullWidthStyles}
        href={localizedHref}
    >
        <FormattedMessage
            id="global-nav.contact-sales-cta.label"
            defaultMessage={label}
        />
    </Button>
);
